import { createContext, useContext } from "react";
import catalogue from "./copy-catalogue.json";
import type { PortalTheme } from "./api";

/**
 * Every guest-facing string on the portal, and the operator's text for it.
 *
 * copy-catalogue.json is the contract: key, today's exact wording, a length
 * limit and the placeholders a value may use. The SAME file is embedded in
 * radhotspotapi (which refuses a save that breaks a rule) and radportal (which
 * drops a bad entry); each repository's tests pin its SHA-256. Change it in all
 * three or in none. Design: radius-wiki portal-brand-from-logo.md, "Portal text
 * per theme".
 *
 * 🔑 THE TEXT IS NEVER MARKUP. Values reach the page as React text nodes, so an
 * operator's "<b>" is shown as the four characters it is.
 *
 * No JSX: this file is part of the portal bundle and the console reads it too.
 */

export type CopyGroup = "page" | "login" | "register" | "forgot" | "myplan" | "recharge" | "payment" | "online" | "messages" | "legal";

export interface CopyEntry {
  key: string;
  group: CopyGroup;
  label: string;
  max: number;
  default: string;
  vars?: string[];
  multiline?: boolean;
}

export type Copy = Readonly<Record<string, string>>;

export const COPY_ENTRIES: readonly CopyEntry[] = catalogue.entries as CopyEntry[];
const BY_KEY = new Map(COPY_ENTRIES.map((e) => [e.key, e]));

export const copyEntry = (key: string): CopyEntry | undefined => BY_KEY.get(key);

/**
 * The four texts a hotspot's legacy theme tables already set (wifiheading,
 * wifiheading1/2, regheading, regalert). Left empty in a theme, the page shows
 * the hotspot's own text for these, not the catalogue default.
 */
export const LEGACY_COPY_KEYS: readonly string[] = ["head.title", "head.subtitle", "register.heading", "register.lead"];

export const DEFAULT_COPY: Copy = Object.fromEntries(COPY_ENTRIES.map((e) => [e.key, e.default]));

/** Keys and values together, in UTF-8 bytes. Same bound, same count, as the Go services. */
export const MAX_COPY_BYTES = 32000;

const PLACEHOLDER = /\{([^{}]*)\}/g;

/** Any C0 control character or DEL other than the line feed -- the same test as the Go services. */
function hasControl(v: string): boolean {
  for (const ch of v) {
    const c = ch.codePointAt(0)!;
    if ((c < 0x20 && c !== 0x0a) || c === 0x7f) return true;
  }
  return false;
}

const utf8Bytes = (s: string) => new TextEncoder().encode(s).length;

/** CRLF and CR become LF; surrounding whitespace goes. An empty result means "not set". */
export function normaliseCopyValue(v: string): string {
  return v.replace(/\r\n?/g, "\n").trim();
}

/** Why `value` cannot be the text for `key`, or null when it can. */
export function copyProblem(key: string, value: string): string | null {
  const e = BY_KEY.get(key);
  if (!e) return `"${key}" is not a portal text`;
  const v = normaliseCopyValue(value);
  if ([...v].length > e.max) return `At most ${e.max} characters.`;
  if (!e.multiline && v.includes("\n")) return "Must be one line.";
  if (hasControl(v)) return "Contains a control character.";
  const allowed = e.vars ?? [];
  for (const m of v.matchAll(PLACEHOLDER)) {
    if (!allowed.includes(m[1]!)) {
      return allowed.length
        ? `Only ${allowed.map((n) => `{${n}}`).join(", ")} can be used here.`
        : "{…} cannot be used in this text.";
    }
  }
  return null;
}

export function copyBytes(copy: Record<string, string>): number {
  return Object.entries(copy).reduce((n, [k, v]) => n + utf8Bytes(k) + utf8Bytes(v), 0);
}

/**
 * Only what a page may render: known keys with valid, non-empty values, each
 * bad entry dropped on its own. Over the size bound, nothing at all -- the
 * same answer radportal gives, whatever order the entries arrive in.
 */
export function cleanCopy(input: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!input || typeof input !== "object" || Array.isArray(input)) return out;
  for (const [k, raw] of Object.entries(input as Record<string, unknown>)) {
    if (typeof raw !== "string") continue;
    const v = normaliseCopyValue(raw);
    if (v === "" || copyProblem(k, v) !== null) continue;
    out[k] = v;
  }
  return copyBytes(out) > MAX_COPY_BYTES ? {} : out;
}

/** `{name}` replaced by vars[name]; a placeholder with no value is left as written. */
export function fill(template: string, vars: Record<string, string> = {}): string {
  return template.replace(PLACEHOLDER, (m, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? vars[name]! : m,
  );
}

/**
 * The text a page renders: catalogue defaults, then the four legacy heading
 * rows radportal composes into the theme, then the published theme's own text.
 */
export function resolveCopy(t: Pick<PortalTheme, "title" | "subtitle" | "registerHeading" | "registerLead" | "copy">): Copy {
  return {
    ...DEFAULT_COPY,
    "head.title": t.title,
    "head.subtitle": t.subtitle,
    "register.heading": t.registerHeading,
    "register.lead": t.registerLead,
    ...cleanCopy(t.copy),
  };
}

export type Translate = (key: string, vars?: Record<string, string>) => string;

export const translator = (copy: Copy): Translate => (key, vars) => fill(copy[key] ?? DEFAULT_COPY[key] ?? key, vars);

export const CopyContext = createContext<Copy>(DEFAULT_COPY);

export function useCopy(): Translate {
  return translator(useContext(CopyContext));
}
